import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Clock, BookOpen, HelpCircle } from 'lucide-react';
import { topicsApi, Topic } from '../api/topics';
import { useProgressStore } from '../state/progressStore';

export const Progress: React.FC = () => {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const { getProgress } = useProgressStore();

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const data = await topicsApi.getAll();
        setTopics(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching topics:', error);
        setTopics([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTopics();
  }, []);

  const getProgressData = () => {
    const safeTopics = Array.isArray(topics) ? topics : [];
    const totalTopics = safeTopics.length;
    const completedTopics = safeTopics.filter(topic => {
      const progress = getProgress(topic.id);
      return progress?.read_pct === 100;
    }).length;
    
    const inProgressTopics = safeTopics.filter(topic => {
      const progress = getProgress(topic.id);
      return progress && progress.read_pct > 0 && progress.read_pct < 100;
    }).length;
    
    return {
      totalTopics,
      completedTopics,
      inProgressTopics,
      notStarted: totalTopics - completedTopics - inProgressTopics
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const progressData = getProgressData();
  const completionPercentage = progressData.totalTopics > 0 
    ? Math.round((progressData.completedTopics / progressData.totalTopics) * 100) 
    : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Mijn Voortgang
        </h1>
        <p className="text-gray-600">
          Overzicht van uw voortgang in de trainingsdelen.
        </p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Totaal Modules</span>
            <BookOpen className="w-5 h-5 text-gray-400" />
          </div>
          <div className="text-3xl font-bold text-gray-900">{progressData.totalTopics}</div>
        </div>

        <div className="bg-white border border-green-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Voltooid</span>
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-3xl font-bold text-green-600">{progressData.completedTopics}</div>
        </div>

        <div className="bg-white border border-blue-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">In Uitvoering</span>
            <Clock className="w-5 h-5 text-blue-600" />
          </div>
          <div className="text-3xl font-bold text-blue-600">{progressData.inProgressTopics}</div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Nog Te Doen</span>
            <HelpCircle className="w-5 h-5 text-gray-400" />
          </div>
          <div className="text-3xl font-bold text-gray-900">{progressData.notStarted}</div>
        </div>
      </div>

      {/* Overall Progress */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Totale Voortgang</h2>
          <span className="text-2xl font-bold text-blue-600">{completionPercentage}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div
            className="bg-blue-600 h-4 rounded-full transition-all duration-300"
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
      </div>

      {/* Module Progress List */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Modules Overzicht</h2>
        <div className="space-y-4">
          {topics.map((topic) => {
            const progress = getProgress(topic.id);
            const progressPct = progress?.read_pct || 0;
            const status = progressPct === 100 ? 'completed' : progressPct > 0 ? 'in-progress' : 'not-started';
            
            return (
              <div key={topic.id} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    {status === 'completed' ? (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : status === 'in-progress' ? (
                      <Clock className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    ) : (
                      <div className="w-5 h-5 border-2 border-gray-300 rounded-full flex-shrink-0" />
                    )}
                    <div>
                      <Link 
                        to={`/topic/${topic.slug}`}
                        className="font-medium text-gray-900 hover:text-blue-600 transition-colors"
                      >
                        {topic.title}
                      </Link>
                      {topic.theme && (
                        <p className="text-sm text-gray-500">Thema: {topic.theme.title}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">{progressPct}%</div>
                      {progress?.last_quiz_score && (
                        <div className="text-xs text-gray-500">Quiz: {progress.last_quiz_score}%</div>
                      )}
                    </div>
                    <div className="w-32">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            status === 'completed' ? 'bg-green-600' :
                            status === 'in-progress' ? 'bg-blue-600' :
                            'bg-gray-300'
                          }`}
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          
          {topics.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Geen trainingsdelen gevonden.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
